import {
  browserPageLifecycle,
  createSyncingProgressRepo,
  onSignIn,
  purgeProgressForUser as purgeStoredProgress,
} from '@dcs/platform/progress';
import type {
  ProgressTransport,
  SyncEndReason,
  SyncTimers,
  SyncingProgressRepo,
} from '@dcs/platform/progress';
import { useEffect } from 'react';
import { getAppProgress } from './app-progress';
import type { AppProgress } from './app-progress';

/** Classes the app offers (`?hang=` in the theory screens); the server's own classes are added. */
export const SYNCED_LICENSE_CLASSES = ['B', 'C1', 'C', 'D'] as const;

/** How long the explicit sign-out waits for the last flush before signing out anyway. */
export const SIGN_OUT_FLUSH_TIMEOUT_MS = 3000;

/** Everything `startProgressSync` takes from the outside; all defaulted for the browser. */
export interface ProgressSyncDeps {
  app: AppProgress;
  userId: string;
  transport: ProgressTransport;
  now: () => number;
  timers: SyncTimers;
  onPageHide: ((flush: () => void) => () => void) | undefined;
  onError: ((error: unknown) => void) | undefined;
}

/**
 * Why the sync stops: how the session ended (`SyncEndReason`: `sign-out-explicit` purges the
 * account's progress from this browser, `session-ended` keeps a 7-day stash of the unsent classes
 * for that user), or `dispose` (unmount / page going away: local data kept).
 */
export type ProgressSyncStopReason = SyncEndReason | 'dispose';

function browserDeps(
  userId: string,
  createTransport: () => ProgressTransport,
  overrides: Partial<Omit<ProgressSyncDeps, 'userId'>>,
): ProgressSyncDeps {
  return {
    app: getAppProgress(),
    userId,
    transport: createTransport(),
    now: () => Date.now(),
    timers: {
      setTimeout: (callback, ms) => window.setTimeout(callback, ms),
      clearTimeout: (handle) => window.clearTimeout(handle as number),
    },
    onPageHide: browserPageLifecycle(window, document),
    onError: undefined,
    ...overrides,
  };
}

interface RunningSync {
  userId: string;
  app: AppProgress;
  repo: SyncingProgressRepo;
  timers: SyncTimers;
  stop: (reason: ProgressSyncStopReason) => void;
}

/**
 * Swaps the app's progress repo for the syncing one of `deps.userId` and runs the sign-in merge.
 * Returns the stop function (back to local only). Not React: `useProgressSync` is the thin wrapper.
 */
export function startProgressSync(deps: ProgressSyncDeps): {
  repo: SyncingProgressRepo;
  stop: (reason: ProgressSyncStopReason) => void;
  /** Settles when the sign-in merge is done (never rejects). */
  signedIn: Promise<void>;
} {
  const { app } = deps;
  const repo = createSyncingProgressRepo({
    local: app.local,
    storage: app.storage,
    userId: deps.userId,
    transport: deps.transport,
    now: deps.now,
    timers: deps.timers,
    licenseClasses: SYNCED_LICENSE_CLASSES,
    ...(deps.onPageHide ? { onPageHide: deps.onPageHide } : {}),
    onError: (error) => deps.onError?.(error),
  });
  app.repo.use(repo);
  const signedIn = onSignIn(repo).catch((error: unknown) => deps.onError?.(error));
  return {
    repo,
    signedIn,
    stop(reason) {
      if (reason === 'dispose') repo.dispose();
      else repo.signOut(reason);
      app.repo.use(app.local);
    },
  };
}

// One sync per page (the hook is mounted once, in `AuthProvider`); module state so the sign-out
// and account-deletion flows can reach it without a provider.
let running: RunningSync | null = null;
let explicitSignOut = false;

function stopRunning(reason: ProgressSyncStopReason): void {
  const current = running;
  running = null;
  current?.stop(reason);
}

/**
 * Explicit sign-out, step 1 (before Better Auth's `signOut`, while the session still works): marks
 * the next session end as explicit and tries ONE flush of the dirty classes, keeping the 30 s
 * spacing (a class PUT less than 30 s ago is not sent: those < 30 s of changes are lost, by design).
 * Waits at most `SIGN_OUT_FLUSH_TIMEOUT_MS`. Never rejects.
 */
export async function prepareProgressSignOut(): Promise<void> {
  explicitSignOut = true;
  const current = running;
  if (current === null) return;
  let handle: unknown;
  const timeout = new Promise<void>((resolve) => {
    handle = current.timers.setTimeout(resolve, SIGN_OUT_FLUSH_TIMEOUT_MS);
  });
  await Promise.race([current.repo.flush(), timeout]);
  current.timers.clearTimeout(handle);
}

/** Explicit sign-out, step 2 (Better Auth confirmed): purges the account's progress now. */
export function finishProgressSignOut(): void {
  stopRunning('sign-out-explicit');
  explicitSignOut = false;
}

/** Explicit sign-out failed: the user is still signed in, the next session end is not explicit. */
export function cancelProgressSignOut(): void {
  explicitSignOut = false;
}

/**
 * Removes everything this browser keeps of `userId`'s progress (local records if they are theirs,
 * sync marks, owner marker, session-expiry stash), stopping their sync first. For the account
 * deletion flow (T-2.5), after the server confirmed the deletion.
 */
export function purgeProgressForUser(userId: string): void {
  const app = running?.app ?? getAppProgress();
  if (running?.userId === userId) stopRunning('sign-out-explicit');
  purgeStoredProgress({
    storage: app.storage,
    userId,
    licenseClasses: SYNCED_LICENSE_CLASSES,
    local: app.local,
  });
}

/**
 * Keeps study progress in sync with the account of `userId`. Mounted once in `AuthProvider`:
 *
 *  - a user id: signed in, sync on (restarted when it changes to another user);
 *  - `null`: signed out (or a guest): a running sync stops. After `prepareProgressSignOut` that is
 *    an explicit sign-out (purge); otherwise the session ended by itself (`session-ended`: the
 *    unsent classes are stashed for that user for 7 days). Either way the account's progress
 *    leaves the screens and the local records;
 *  - `undefined`: not known yet (the session is loading): nothing changes, so a session check never
 *    clears anything.
 *
 * Unmounting stops with `dispose` (local data kept). The merge runs each time the user becomes
 * signed in (also on every page load that has a session); screens keep reading the local copy.
 * `createTransport` is called once per sign-in; `overrides` is for tests (clock, timers, app).
 */
export function useProgressSync(
  userId: string | null | undefined,
  createTransport: () => ProgressTransport,
  overrides: Partial<Omit<ProgressSyncDeps, 'userId'>> = {},
): void {
  useEffect(() => {
    if (userId === undefined || running?.userId === userId) return;
    // Signed out, or another account signed in: the previous account leaves this browser.
    if (running !== null) stopRunning(explicitSignOut ? 'sign-out-explicit' : 'session-ended');
    explicitSignOut = false;
    if (userId === null) return;
    const deps = browserDeps(userId, createTransport, overrides);
    const { repo, stop } = startProgressSync(deps);
    running = { userId, app: deps.app, repo, timers: deps.timers, stop };
    // Only the user restarts the sync; the factory and overrides are read once per sign-in.
  }, [userId]);

  useEffect(
    () => () => {
      stopRunning('dispose');
    },
    [],
  );
}
